
  /**
   * @license
   * author: epignosis front-end team
   * @epignosis_llc/gnosis.js v6.0.0
   * Released under the MIT license.
   */

import { objectWithoutProperties as _objectWithoutProperties, defineProperty as _defineProperty, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import '../../../node_modules/core-js/modules/es.array.map.js';
import { Children, cloneElement, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import FocusLock from '../../../node_modules/react-focus-lock/dist/es2015/Combination.js';
import classNames from '../../../node_modules/classnames/index.js';
import { drawerContainer } from './styles.js';
import Header from './components/Header.js';
import Body from './components/Body.js';
import Mask from './components/Mask.js';
import Footer from './components/Footer.js';
import { createElement as jsx } from '../../../node_modules/@emotion/react/dist/emotion-react.browser.esm.js';
import { AnimatePresence } from '../../../node_modules/framer-motion/dist/es/components/AnimatePresence/index.mjs.js';
import { m } from '../../../node_modules/framer-motion/dist/es/render/dom/motion-minimal.mjs.js';
import { domAnimation } from '../../../node_modules/framer-motion/dist/es/render/dom/features-animation.mjs.js';
import { LazyMotion } from '../../../node_modules/framer-motion/dist/es/components/LazyMotion/index.mjs.js';

var _excluded = ["isOpen", "placement", "closeOnOutsideClick", "showMask", "width", "dialogStyles", "dialogClassName", "disableFocusLock", "children", "onClose"];
var dialogVariants = {
  initial: function initial(_ref) {
    var placement = _ref.placement,
      width = _ref.width;
    return {
      opacity: 0,
      x: placement === "right" ? "100%" : "-".concat(width),
      transition: {
        type: "tween"
      }
    };
  },
  expanded: function expanded() {
    return {
      opacity: 1,
      x: 0,
      transition: {
        type: "tween"
      }
    };
  }
};
var DRAWER_ROOT = "drawerRoot";
var DrawerRoot = function DrawerRoot() {
  return jsx("div", {
    id: DRAWER_ROOT
  });
};
var Drawer = function Drawer(props) {
  var isOpen = props.isOpen,
    _props$placement = props.placement,
    placement = _props$placement === void 0 ? "left" : _props$placement,
    _props$closeOnOutside = props.closeOnOutsideClick,
    closeOnOutsideClick = _props$closeOnOutside === void 0 ? true : _props$closeOnOutside,
    _props$showMask = props.showMask,
    showMask = _props$showMask === void 0 ? true : _props$showMask,
    _props$width = props.width,
    width = _props$width === void 0 ? "31.5rem" : _props$width,
    dialogStyles = props.dialogStyles,
    dialogClassName = props.dialogClassName,
    _props$disableFocusLo = props.disableFocusLock,
    disableFocusLock = _props$disableFocusLo === void 0 ? false : _props$disableFocusLo,
    children = props.children,
    onClose = props.onClose,
    rest = _objectWithoutProperties(props, _excluded);
  var clonedChildren = Children.map(children, function (child) {
    var _props;
    return child && /*#__PURE__*/cloneElement(child, {
      onClose: ((_props = child.props) === null || _props === void 0 ? void 0 : _props.closable) && onClose
    });
  });
  var drawerEl = document.getElementById(DRAWER_ROOT);
  var dialogClassNames = classNames(_defineProperty({
    dialog: true,
    "placement-left": placement === "left",
    "placement-right": placement === "right"
  }, dialogClassName !== null && dialogClassName !== void 0 ? dialogClassName : "", dialogClassName));
  var drawerRef = useRef(null);
  var handleCloseOnOutsideClick = function handleCloseOnOutsideClick(e) {
    if (!closeOnOutsideClick) return;
    e.nativeEvent.stopImmediatePropagation();
    onClose();
  };
  useEffect(function () {
    if (!isOpen) return;
    var handleEscape = function handleEscape(event) {
      if (event.key === "Escape") {
        onClose();
      }
    };
    var handleClickOutside = function handleClickOutside(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        handleCloseOnOutsideClick(e);
      }
    };
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);
    return function () {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);
  var drawer = jsx(LazyMotion, {
    features: domAnimation
  }, jsx(AnimatePresence, null, isOpen && jsx("div", _extends({
    css: function css(theme) {
      return drawerContainer(theme, width);
    },
    "data-testid": "drawer",
    ref: drawerRef
  }, rest), showMask && jsx(Mask, {
    onClose: handleCloseOnOutsideClick
  }), jsx(FocusLock, {
    returnFocus: true,
    disabled: !isOpen || disableFocusLock
  }, jsx(m.dialog, {
    id: "drawer-dialog",
    style: dialogStyles,
    className: dialogClassNames,
    "aria-expanded": isOpen,
    "aria-hidden": !isOpen,
    "aria-modal": "true",
    initial: "initial",
    animate: "expanded",
    exit: "initial",
    variants: dialogVariants,
    custom: {
      placement: placement,
      width: width
    }
  }, clonedChildren)))));
  useEffect(function () {
    if (isOpen) {
      document.querySelector("body").style.overflow = "hidden";
    } else {
      document.querySelector("body").style.overflow = "";
    }
  }, [isOpen]);
  useEffect(function () {
    return function () {
      document.querySelector("body").style.overflow = "";
    };
  }, []);
  return drawerEl && /*#__PURE__*/createPortal(drawer, drawerEl);
};
Drawer.Header = Header;
Drawer.Body = Body;
Drawer.Footer = Footer;
Drawer.Root = DrawerRoot;

export { Drawer as default };
//# sourceMappingURL=Drawer.js.map
